import { useState } from "react";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";
import { Loader2, X, Copy, Mail, MessageCircle, Check, Link as LinkIcon } from "lucide-react";

const money = (n) => `£${Number(n).toLocaleString("en-GB", { maximumFractionDigits: 2 })}`;

export default function PaymentLinkDialog({ booking, onClose }) {
  const [mode, setMode] = useState("deposit");
  const [loading, setLoading] = useState(false);
  const [link, setLink] = useState(null); // { url, amount, mode, session_id }
  const [copied, setCopied] = useState(false);

  const price = booking.manual_price_override ?? booking.instant_booking_price;
  const paid = Number(booking.deposit_paid || 0);
  const remainingFull = Math.max(0, Number(price || 0) - paid);
  const depositAmount = Math.ceil((Number(price || 0) * 0.30) / 10) * 10;

  async function generate() {
    setLoading(true);
    try {
      const originUrl = window.location.origin;
      const { data } = await apiClient.post(`/admin/bookings/${booking.booking_ref}/payment-link`, {
        mode,
        origin_url: originUrl,
      });
      setLink(data);
      toast.success("Payment link ready.");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not create payment link.");
    } finally {
      setLoading(false);
    }
  }

  async function copyLink() {
    if (!link?.url) return;
    try {
      await navigator.clipboard.writeText(link.url);
      setCopied(true);
      toast.success("Link copied.");
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      toast.error("Clipboard blocked — long-press to copy manually.");
    }
  }

  function whatsappHref() {
    if (!link?.url) return "#";
    const phone = (booking.customer_phone || "").replace(/[^\d+]/g, "");
    const msg = encodeURIComponent(
      `Hi ${booking.customer_name?.split(" ")[0] || ""} — MHJ Prestige Transport.\n\n` +
      `Your secure ${link.mode === "full" ? "payment" : "deposit"} link for booking ${booking.booking_ref} (${money(link.amount)}):\n\n${link.url}\n\n` +
      `Once paid we'll confirm collection. Reply here with any questions.`
    );
    return `https://wa.me/${phone.replace(/^\+/, "")}${phone ? "" : ""}?text=${msg}`;
  }

  function mailtoHref() {
    if (!link?.url) return "#";
    const subject = encodeURIComponent(`Your MHJ Prestige Transport payment link (${booking.booking_ref})`);
    const body = encodeURIComponent(
      `Hi ${booking.customer_name || ""},\n\nThank you for choosing MHJ Prestige Transport.\n\n` +
      `Your secure ${link.mode === "full" ? "payment" : "deposit"} link for booking ${booking.booking_ref} (${money(link.amount)}):\n\n${link.url}\n\n` +
      `Once payment is complete we'll confirm collection.\n\nMHJ Prestige Transport`
    );
    return `mailto:${booking.customer_email || ""}?subject=${subject}&body=${body}`;
  }

  return (
    <Backdrop onClose={onClose}>
      <div
        data-testid="admin-payment-link-modal"
        className="w-full max-w-lg border border-white/15 bg-[#0A0A0A] p-6 md:p-8 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-6 mb-6">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#E60000] font-bold">
              Send Payment Link · {booking.booking_ref}
            </div>
            <h2 className="font-display font-extrabold text-2xl md:text-3xl mt-2">
              {money(price)} agreed
            </h2>
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="admin-payment-link-close">
            <X className="w-5 h-5 text-zinc-400 hover:text-white" />
          </button>
        </div>

        {!link && (
          <>
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-3">
              Payment amount
            </div>
            <div className="grid grid-cols-2 gap-3">
              <ModeOption
                testid="admin-payment-mode-deposit"
                active={mode === "deposit"}
                onClick={() => setMode("deposit")}
                title="Deposit only"
                subtitle="Default"
                amount={money(depositAmount)}
                note="30% rounded up to £10"
              />
              <ModeOption
                testid="admin-payment-mode-full"
                active={mode === "full"}
                onClick={() => setMode("full")}
                title="Full payment"
                subtitle="Deliberate choice"
                amount={money(remainingFull)}
                note={paid > 0 ? `Remaining after deposit paid` : "Entire agreed price"}
              />
            </div>

            <button
              data-testid="admin-payment-generate"
              onClick={generate}
              disabled={loading}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#E60000] hover:bg-[#FF1A1A] px-5 py-4 text-xs tracking-[0.3em] uppercase font-bold text-white transition-colors disabled:opacity-50"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <LinkIcon className="w-4 h-4" />}
              Generate secure link
            </button>
          </>
        )}

        {link && (
          <div className="space-y-5" data-testid="admin-payment-link-ready">
            <div className="border border-white/10 bg-[#050505] p-4">
              <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold">
                Ready to send — {link.mode === "full" ? "Full payment" : "Deposit"} · {money(link.amount)}
              </div>
              <div className="text-white text-xs break-all mt-2 font-mono">{link.url}</div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <button
                data-testid="admin-payment-copy"
                onClick={copyLink}
                className="inline-flex items-center justify-center gap-2 border border-white/15 bg-transparent hover:border-[#E60000] px-4 py-3 text-xs tracking-[0.2em] uppercase font-bold text-white transition-colors"
              >
                {copied ? <Check className="w-4 h-4 text-[#E60000]" /> : <Copy className="w-4 h-4" />}
                {copied ? "Copied" : "Copy link"}
              </button>
              <a
                data-testid="admin-payment-whatsapp"
                href={whatsappHref()}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 border border-white/15 bg-transparent hover:border-[#E60000] px-4 py-3 text-xs tracking-[0.2em] uppercase font-bold text-white transition-colors"
              >
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
              <a
                data-testid="admin-payment-email"
                href={mailtoHref()}
                className="inline-flex items-center justify-center gap-2 border border-white/15 bg-transparent hover:border-[#E60000] px-4 py-3 text-xs tracking-[0.2em] uppercase font-bold text-white transition-colors"
              >
                <Mail className="w-4 h-4" /> Email
              </a>
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => { setLink(null); setCopied(false); }}
                className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 hover:text-white"
              >
                Generate a different link
              </button>
            </div>
          </div>
        )}
      </div>
    </Backdrop>
  );
}

function Backdrop({ children, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start md:items-center justify-center overflow-y-auto py-8 px-4"
      onClick={onClose}
    >
      {children}
    </div>
  );
}

function ModeOption({ testid, active, onClick, title, subtitle, amount, note }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`text-left border p-4 transition-colors ${
        active ? "border-[#E60000] bg-[#E60000]/5" : "border-white/15 hover:border-white/40"
      }`}
    >
      <div className="flex justify-between items-start">
        <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold">{subtitle}</div>
        {active && <div className="w-2 h-2 rounded-full bg-[#E60000]" />}
      </div>
      <div className="mt-1 text-white font-bold text-sm">{title}</div>
      <div className="mt-2 font-display text-2xl font-extrabold text-white">{amount}</div>
      <div className="text-[10px] text-zinc-500 mt-1">{note}</div>
    </button>
  );
}
